import React, {useEffect, useState, useCallback} from 'react';
import Modal from "@components/modal/modal.component";
import {useTranslation} from "react-i18next";
import {Controller, useForm} from "react-hook-form";
import Form from "@components/form";
import {IonCol, IonRow, IonSelectOption} from "@ionic/react";
import TimeInput from "@components/form/input/time.component";
import {StyledButton} from "@components/button/button.style";
import {StyledIonSelect} from "@components/form/input/select.style";
import {StyledIonLabel} from "@components/form/input/label.style";
import {addAvailability, computeMinAvailabilityDate, getRepeatTypeOptions, isAvailabilityPreferredPlacesEnabled} from "@services/calendar/availability.service";
import moment from "moment";
import {AvailabilityForm} from "@models/availability";
import {mergeDateWithTime} from "@utils/tools/time";
import {useToast} from "@context/toast.context";
import DateInput from "@components/form/input/date.component";
import {StyledTextHref} from "@components/text/text.styles";
import PreferredPlacesOfServiceListModal from "@app/availabilityPage/modals/preferredPlacesOfServiceListModal.component";
import {getAvailabilityErrorMessage} from "@services/calendar/availability.service";
import {useRepeatEndDate} from "@hooks/calendar/useRepeatEndDate";
import {useEndTimeCalculate} from "@hooks/calendar/useEndTimeCalculate";

type AvailabilityAddModalProps = {
    isOpen: boolean,
    onClose: () => void,
    onDone: () => void,
    selectedDate: string,
    hoursForAvailability?: number | null,
}

const AvailabilityAddModal: React.FC<AvailabilityAddModalProps> = ({isOpen, onClose, onDone, selectedDate, hoursForAvailability}: AvailabilityAddModalProps) => {
    const {t} = useTranslation();
    const {showToast} = useToast();

    const {control, handleSubmit, errors, setValue, watch, reset, register} = useForm<AvailabilityForm>(
        {
            mode: 'onSubmit',
            defaultValues: {
                duration: undefined,
                endTime: undefined,
                preferredPlacesOfService: [],
            },
        }
    );

    const startTime = watch('startTime');
    const endTime = watch('endTime');
    const repeatType = watch('repeatType');

    const [showPreferredPlacesOfServiceModal, setShowPreferredPlacesOfServiceModal] = useState(false);
    const [preferredPlacesEnabled, setPreferredPlacesEnabled] = useState(false);

    useEndTimeCalculate({startTime, endTime, setValue});

    const maxRepeatEndDate = useRepeatEndDate({repeatType, startTime, watch, setValue});

    useEffect(() => {
        isAvailabilityPreferredPlacesEnabled().then(setPreferredPlacesEnabled);
    }, []);

    useEffect(() => {
        if (preferredPlacesEnabled) {
            register('preferredPlacesOfService');
            register('agreementForOuterPlaces');
        }
    }, [register, preferredPlacesEnabled]);

    const onSubmit = useCallback(async (data: AvailabilityForm) => {
        if (preferredPlacesEnabled && !data.preferredPlacesOfService?.length) {
            showToast({
                message: t('availability.error.selectAtLeastOnePlaceOfService'),
                type: 'danger',
                duration: 3000,
            });

            return;
        }

        try {
            const start = moment(data.startTime);
            const end = moment(data.endTime);
            const duration = end.diff(start, 'minutes');

            const dataToSend = {
                ...data,
                duration,
            };

            await addAvailability(dataToSend);

            showToast({
                message: t('availability.added'),
                type: 'success',
                duration: 3000,
            });

            reset();
            onDone();
            onClose();
        } catch (error) {
            const message = getAvailabilityErrorMessage(error, false);

            showToast({
                message: t(message),
                type: 'danger',
                duration: 3000,
            });

            console.error(error);
        }
    }, [showToast, t, reset, onDone, onClose, preferredPlacesEnabled]);

    return (
        <Modal
            initialBreakpoint={0.6}
            breakpoints={[0, 0.6]}
            isOpen={isOpen}
            onDidDismiss={() => onClose()}
        >
            {preferredPlacesEnabled && (
                <PreferredPlacesOfServiceListModal
                    isOpen={showPreferredPlacesOfServiceModal}
                    onClose={() => setShowPreferredPlacesOfServiceModal(false)}
                    preSelected={watch('preferredPlacesOfService')}
                    isAgreementForOuterPlaces={watch('agreementForOuterPlaces')}
                    setValue={(selectedPlaces, agreementForOuterPlaces) => {
                        const ids = selectedPlaces.map(p => p.id);

                        setValue('preferredPlacesOfService', ids);
                        setValue('agreementForOuterPlaces', agreementForOuterPlaces);
                    }}
                />
            )}
            <Form.Container onSubmit={handleSubmit(onSubmit)}>
                <IonRow>
                    <IonCol size="12" className="label" style={{margin: "10px 0"}}>
                        {t('availability.addAvailabilityTitle')} - {moment(selectedDate).format('DD-MM-YYYY')}
                    </IonCol>
                </IonRow>
                <IonRow>
                    <IonCol size="3" className="label">
                        <Controller
                            name="startTime"
                            control={control}
                            rules={{
                                required: true,
                                validate: (value) => {
                                    if (!value) return false;
                                    if (!hoursForAvailability) return true;

                                    const minDate = computeMinAvailabilityDate(hoursForAvailability);
                                    const selected = moment(value);

                                    if (selected.isBefore(minDate)) {
                                        showToast({
                                            message: t('availability.error.hoursForAvailabilityAddExceeded'),
                                            type: 'danger',
                                            duration: 3000,
                                        });

                                        return false;
                                    }

                                    return true;
                                },
                            }}
                            render={(field) => (
                                <TimeInput
                                    keepContentsMounted={true}
                                    cancelText={t("common.dateCancel")}
                                    doneText={t("common.dateDone")}
                                    placeholder={t("availability.startTime.placeholder")}
                                    onChange={(date) => field.onChange(mergeDateWithTime(date, selectedDate))}
                                    value={field.value}
                                    minuteValues="0,15,30,45"
                                />
                            )}
                        />
                    </IonCol>
                    <IonCol size="3" className="label">
                        <Controller
                            name="endTime"
                            control={control}
                            rules={{required: true}}
                            render={(field) => (
                                <TimeInput
                                    keepContentsMounted={true}
                                    cancelText={t("common.dateCancel")}
                                    doneText={t("common.dateDone")}
                                    placeholder={t("availability.endTime.placeholder")}
                                    onChange={(date) => field.onChange(mergeDateWithTime(date, selectedDate))}
                                    value={field.value}
                                    minuteValues="0,15,30,45"
                                />
                            )}
                        />
                    </IonCol>
                    <IonCol size="6" className="label">
                        <Controller
                            name="repeatType"
                            control={control}
                            rules={{required: true}}
                            render={(field) => (
                                <StyledIonSelect
                                    placeholder={t('availability.selectRepeatTypeOption')}
                                    onIonChange={(e) => field.onChange(e.detail.value)}
                                    onIonBlur={field.onBlur}
                                    value={field.value}
                                    className={errors.repeatType ? 'error' : ''}
                                >
                                    {
                                        getRepeatTypeOptions().map((repeatOption) => {
                                            return <IonSelectOption
                                                key={repeatOption.key}
                                                value={repeatOption.key}>{t(repeatOption.label)}</IonSelectOption>
                                        })
                                    }
                                </StyledIonSelect>
                            )}
                        />
                    </IonCol>
                </IonRow>
                {
                    repeatType && repeatType !== 'DONT_REPEAT' ? (
                        <>
                            <IonRow className="mt-8">
                                <IonCol size="12">
                                    <StyledIonLabel>
                                        {t('availability.repeatEndDate.label')}
                                    </StyledIonLabel>
                                </IonCol>
                            </IonRow>
                            <IonRow>
                                <IonCol size="12" className="label">
                                    <Controller
                                        name={'repeatEndDate'}
                                        control={control}
                                        rules={{required: false}}
                                        render={(field) => (
                                            <DateInput
                                                presentation="date"
                                                format="DD.MM.YYYY"
                                                onChange={(value) => field.onChange(moment(value).format('YYYY-MM-DD'))}
                                                min={moment(startTime).add(1, 'day').format('YYYY-MM-DD')}
                                                max={maxRepeatEndDate}
                                                cancelText={t("common.dateCancel")}
                                                doneText={t("common.dateDone")}
                                                placeholder={t("common.select")}
                                                value={field.value}
                                            />
                                        )}
                                    />
                                </IonCol>
                            </IonRow>
                        </>
                    ) : <></>
                }
                {preferredPlacesEnabled ? (
                    <IonRow>
                        <IonCol size="12">
                            <StyledTextHref className="full-width" onClick={() => setShowPreferredPlacesOfServiceModal(true)}>{t('availability.preferredPlacesOfService.label')}</StyledTextHref>
                        </IonCol>
                    </IonRow>
                ) : <></>}
                <IonRow>
                    <StyledButton type="submit">{t(('availability.add'))}</StyledButton>
                </IonRow>
            </Form.Container>
        </Modal>
    );
};

export default AvailabilityAddModal;